// src/components/system/seo/SEOManager.js
// SEO Management System
// Created: 2025-01-20 16:49:00 UTC+8
// Purpose: Dynamic SEO management based on website configuration

import { Helmet } from 'react-helmet';
import React from 'react';

/**
 * SEO Manager Class
 * Handles dynamic SEO configuration based on website settings
 */
export class SEOManager {
  constructor(config) {
    this.config = config;
  }

  /**
   * Update configuration
   * @param {Object} config - New configuration
   */
  updateConfig(config) {
    this.config = config;
  }

  /**
   * Generate meta tags based on configuration
   * @param {Object} pageData - Page-specific data to override defaults
   * @returns {Object} - Meta tags object
   */
  generateMetaTags(pageData = {}) {
    const { website, seo } = this.config;
    const { meta } = seo;

    return {
      title: pageData.title || website.title,
      description: pageData.description || website.description,
      keywords: pageData.keywords || meta.keywords,
      author: meta.author,
      robots: meta.robots,
      viewport: meta.viewport,
      charset: meta.charset,
      language: pageData.language || meta.language,
      canonical: pageData.canonical || `${website.url}${pageData.path || ''}`
    };
  }

  /**
   * Generate Open Graph tags
   * @param {Object} pageData - Page-specific data
   * @returns {Object} - Open Graph tags
   */
  generateOpenGraphTags(pageData = {}) {
    const { website, seo } = this.config;
    const { openGraph } = seo;

    return {
      'og:type': pageData.type || openGraph.type,
      'og:site_name': openGraph.siteName,
      'og:title': pageData.title || openGraph.title,
      'og:description': pageData.description || openGraph.description,
      'og:image': pageData.image ? `${website.url}${pageData.image}` : `${website.url}${openGraph.image}`,
      'og:image:alt': pageData.imageAlt || openGraph.imageAlt,
      'og:url': pageData.canonical || `${website.url}${pageData.path || ''}`,
      'og:locale': pageData.locale || openGraph.locale
    };
  }

  /**
   * Generate Twitter Card tags
   * @param {Object} pageData - Page-specific data
   * @returns {Object} - Twitter Card tags
   */
  generateTwitterTags(pageData = {}) {
    const { website, seo } = this.config;
    const { twitter } = seo;

    return {
      'twitter:card': twitter.card,
      'twitter:site': twitter.site,
      'twitter:creator': twitter.creator,
      'twitter:title': pageData.title || twitter.title,
      'twitter:description': pageData.description || twitter.description,
      'twitter:image': pageData.image ? `${website.url}${pageData.image}` : `${website.url}${twitter.image}`
    };
  }

  /**
   * Generate structured data (JSON-LD)
   * @param {Object} pageData - Page-specific data
   * @returns {Object} - Structured data object
   */
  generateStructuredData(pageData = {}) {
    const { website, seo, contact } = this.config;
    const { structuredData } = seo;

    const baseStructuredData = {
      ...structuredData,
      name: pageData.organizationName || structuredData.name,
      url: website.url,
      logo: `${website.url}${website.logo}`,
      description: pageData.description || structuredData.description
    };

    // Add contact information if available
    if (contact.email || contact.phone) {
      baseStructuredData.contactPoint = {
        '@type': 'ContactPoint',
        email: contact.email,
        telephone: contact.phone,
        contactType: 'customer service'
      };
    }

    // Add page-specific structured data
    if (pageData.type === 'article') {
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: pageData.title,
        description: pageData.description,
        image: pageData.image ? `${website.url}${pageData.image}` : undefined,
        author: {
          '@type': 'Organization',
          name: structuredData.name
        },
        publisher: {
          '@type': 'Organization',
          name: structuredData.name,
          logo: {
            '@type': 'ImageObject',
            url: `${website.url}${website.logo}`
          }
        },
        datePublished: pageData.datePublished,
        dateModified: pageData.dateModified || pageData.datePublished
      };
    }

    return baseStructuredData;
  }

  /**
   * Generate security headers
   * @returns {Object} - Security headers
   */
  generateSecurityHeaders() {
    const { security } = this.config.seo;
    
    return {
      'Content-Security-Policy': security.contentSecurityPolicy,
      'X-Frame-Options': security.xFrameOptions,
      'X-Content-Type-Options': security.xContentTypeOptions,
      'Referrer-Policy': security.referrerPolicy
    };
  }

  /**
   * Generate sitemap URLs based on navigation configuration
   * @returns {Array} - Array of sitemap URLs
   */
  generateSitemapUrls() {
    const { website, navigation, i18n } = this.config;
    const urls = [];

    // Generate URLs for each language
    i18n.supportedLanguages.forEach(lang => {
      navigation.menu.forEach(menuItem => {
        urls.push({
          url: `${website.url}${menuItem.path}`,
          changefreq: 'weekly',
          priority: menuItem.path === '/' ? 1.0 : 0.8,
          language: lang,
          alternateUrls: i18n.supportedLanguages.map(altLang => ({
            language: altLang,
            url: `${website.url}${menuItem.path}?lang=${altLang}`
          }))
        });
      });
    });

    return urls;
  }
}

/**
 * SEO Head Component
 * React component that renders SEO tags using Helmet
 */
export const SEOHead = ({ config, pageData = {} }) => {
  // Return basic meta tags if config is not available yet
  if (!config || !config.website || !config.seo) {
    return (
      <Helmet>
        <title>Loading...</title>
        <meta name="description" content="Loading website..." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
    );
  }

  const seoManager = new SEOManager(config);
  const metaTags = seoManager.generateMetaTags(pageData);
  const openGraphTags = seoManager.generateOpenGraphTags(pageData);
  const twitterTags = seoManager.generateTwitterTags(pageData);
  const structuredData = seoManager.generateStructuredData(pageData);
  const securityHeaders = seoManager.generateSecurityHeaders();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaTags.title}</title>
      <meta name="description" content={metaTags.description} />
      <meta name="keywords" content={metaTags.keywords} />
      <meta name="author" content={metaTags.author} />
      <meta name="robots" content={metaTags.robots} />
      <meta name="viewport" content={metaTags.viewport} />
      <meta charSet={metaTags.charset} />
      <meta name="language" content={metaTags.language} />
      <link rel="canonical" href={metaTags.canonical} />
      
      {/* Favicon */}
      {config.website.favicon && (
        <link rel="icon" href={config.website.favicon} />
      )}
      
      {/* Open Graph Tags */}
      {Object.entries(openGraphTags).map(([key, value]) => (
        <meta key={key} property={key} content={value} />
      ))}
      
      {/* Twitter Card Tags */}
      {Object.entries(twitterTags).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
      
      {/* Security Headers */}
      {Object.entries(securityHeaders).map(([key, value]) => (
        <meta key={key} httpEquiv={key} content={value} />
      ))}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Language Alternates */}
      {config.i18n && config.i18n.supportedLanguages && config.i18n.supportedLanguages.map(lang => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${config.website.url}${pageData.path || ''}?lang=${lang}`}
        />
      ))}
    </Helmet>
  );
};

/**
 * Hook for using SEO manager in components
 * @param {Object} config - Website configuration
 * @returns {Object} - SEO manager instance and utilities
 */
export const useSEO = (config) => {
  const seoManager = React.useMemo(() => new SEOManager(config), [config]);
  
  return {
    seoManager,
    generateMetaTags: (pageData) => seoManager.generateMetaTags(pageData),
    generateOpenGraphTags: (pageData) => seoManager.generateOpenGraphTags(pageData),
    generateTwitterTags: (pageData) => seoManager.generateTwitterTags(pageData),
    generateStructuredData: (pageData) => seoManager.generateStructuredData(pageData)
  };
};

export default SEOManager;
